/**
 * CUSTOMER-R4.7 — Repeat / reorder is NEVER the original order.
 * Snapshot available SKUs at current price/stock → merge into the live cart →
 * customer confirms a NEW checkout. Original STG / UUID / payment stay immutable.
 */
import type { CartV2Item } from "./cart-v2.js";
import { currentIdentityScope } from "./customer-profile-v2.js";
import type { CatalogV2Product } from "./catalog-v2-types.js";
import type { CustomerOrderDetail, CustomerOrderItem } from "./order-history-v2.js";
import { resolveCustomerFacingCode } from "./product-identity-v2.js";
import { resolveCustomerVisiblePrice } from "./resolve-customer-visible-price-v2.js";

const SESSION_KEY = "asiastyle.store-v2.reorder-session.v1";
const CART_OP_WINDOW_MS = 4_000;

export type ReorderUnavailableItem = {
  title: string;
  sku: string | null;
  productId: string | null;
};

export type ReorderAvailableItem = {
  cartItem: Omit<CartV2Item, "qty">;
  qty: number;
  currentPriceUah: number;
  sourceUnitPriceUah: number | null;
};

export type ReorderSnapshot = {
  sourcePublicOrderNumber: string;
  sourceOrderStatus: string;
  sourcePaymentStatus: string;
  itemCountSource: number;
  available: ReorderAvailableItem[];
  unavailable: ReorderUnavailableItem[];
  cartValueCurrent: number;
  deliveryCity: string | null;
  deliveryWarehouse: string | null;
};

export type ReorderSourceInput = {
  order: Pick<
    CustomerOrderDetail,
    | "order_id"
    | "display_order_number"
    | "status"
    | "order_top_status"
    | "payment_status"
    | "delivery_city"
    | "delivery_warehouse"
  >;
  items: CustomerOrderItem[];
};

export type ReorderSessionV2 = {
  sourcePublicOrderNumber: string;
  sourceOrderStatus: string;
  sourcePaymentStatus: string;
  itemCountSource: number;
  itemCountAdded: number;
  itemCountUnavailable: number;
  cartValueCurrent: number;
  unavailable: ReorderUnavailableItem[];
  cartHadItemsBefore: boolean;
  deliveryCity: string | null;
  deliveryWarehouse: string | null;
  startedAt: string;
  checkoutOpened: boolean;
  newPublicOrderNumber: string | null;
  abandoned: boolean;
};

let lastCartOp: { stg: string; at: number; opId: string } | null = null;

export function normalizePublicStg(raw: string | null | undefined): string | null {
  const s = String(raw ?? "")
    .trim()
    .toUpperCase();
  const m = s.match(/^(STG-\d{4,12})$/);
  return m?.[1] ?? null;
}

/** Customer-visible STG only. Internal UUID is never an analytics identifier here. */
export function reorderAnalyticsBase(session: Pick<ReorderSnapshot, "sourcePublicOrderNumber" | "sourceOrderStatus" | "sourcePaymentStatus" | "itemCountSource">): Record<string, string | number> {
  return {
    source_public_order_number: session.sourcePublicOrderNumber,
    source_order_status: session.sourceOrderStatus,
    source_payment_status: session.sourcePaymentStatus,
    item_count_source: session.itemCountSource,
  };
}

export function beginReorderCartOperation(
  sourceStg: string,
  now = Date.now(),
  windowMs = CART_OP_WINDOW_MS,
): { apply: boolean; duplicate: boolean; opId: string } {
  const stg = normalizePublicStg(sourceStg) ?? String(sourceStg ?? "").trim();
  if (lastCartOp && lastCartOp.stg === stg && now - lastCartOp.at < windowMs) {
    return { apply: false, duplicate: true, opId: lastCartOp.opId };
  }
  const opId = `reorder-cart:${stg}:${now.toString(36)}`;
  lastCartOp = { stg, at: now, opId };
  return { apply: true, duplicate: false, opId };
}

/** Test helper — never call from production UI. */
export function resetReorderCartOperationForTests(): void {
  lastCartOp = null;
}

function normId(v: string | null | undefined): string {
  return String(v ?? "")
    .trim()
    .toLowerCase();
}

export function matchCatalogProductForReorder(
  catalog: CatalogV2Product[],
  line: CustomerOrderItem,
): CatalogV2Product | null {
  const productId = normId(line.product_id);
  const sku = normId(line.sku);
  const originalSku = normId(line.original_sku);
  const barcode = normId(line.barcode);
  const keys = [productId, sku, originalSku, barcode].filter(Boolean);

  const score = (p: CatalogV2Product): number => {
    const ids = [
      normId(p.id),
      normId(p.promProductId),
      normId(p.sku),
      normId(p.merchantSku),
      normId(p.merchantArticle),
      normId(p.barcodeGtin),
    ];
    if (productId && (ids[0] === productId || ids[1] === productId)) return 100;
    if (sku && (ids[3] === sku || ids[4] === sku || ids[2] === sku)) return 80;
    if (originalSku && (ids[3] === originalSku || ids[4] === originalSku || ids[2] === originalSku)) return 70;
    if (barcode && ids[5] === barcode) return 60;
    if (keys.some((k) => ids.includes(k))) return 40;
    return 0;
  };

  let best: CatalogV2Product | null = null;
  let bestScore = 0;
  for (const p of catalog) {
    const s = score(p);
    if (s > bestScore) {
      best = p;
      bestScore = s;
    }
  }
  return bestScore > 0 ? best : null;
}

export function isReorderSellableNow(product: CatalogV2Product, currentPriceUah: number): boolean {
  return product.isSellable === true && currentPriceUah > 0;
}

function storeArticleOf(product: CatalogV2Product): string | null {
  const facing = resolveCustomerFacingCode({
    merchantSku: product.merchantSku,
    merchantArticle: product.merchantArticle,
    barcodeGtin: product.barcodeGtin,
    barcodeVerification: product.barcodeVerification,
  });
  const article =
    facing && (facing.kind === "merchant_sku" || facing.kind === "merchant_article")
      ? facing.value
      : product.merchantSku?.trim() || product.merchantArticle?.trim() || null;
  return article && article !== product.id ? article : null;
}

export function buildReorderSnapshot(
  source: ReorderSourceInput,
  catalog: CatalogV2Product[],
  titleOf: (p: CatalogV2Product) => string,
): ReorderSnapshot {
  const sourcePublicOrderNumber =
    normalizePublicStg(source.order.display_order_number) ??
    String(source.order.display_order_number ?? "").trim();
  const available: ReorderAvailableItem[] = [];
  const unavailable: ReorderUnavailableItem[] = [];

  for (const line of source.items) {
    const product = matchCatalogProductForReorder(catalog, line);
    const currentPrice = product ? resolveCustomerVisiblePrice(product).primary : 0;
    if (!product || !isReorderSellableNow(product, currentPrice)) {
      unavailable.push({
        title: String(line.title ?? "").trim() || "Товар",
        sku: line.sku ?? null,
        productId: line.product_id ?? null,
      });
      continue;
    }
    available.push({
      qty: Math.max(1, Math.round(Number(line.qty) || 1)),
      currentPriceUah: currentPrice,
      sourceUnitPriceUah: line.unit_price_uah,
      cartItem: {
        id: product.id,
        title: titleOf(product),
        priceUah: currentPrice,
        imageUrl: product.imageUrl,
        sku: storeArticleOf(product),
        brand: product.brand,
        weight: product.weight,
        availabilityLabel: "В наявності",
        ...(product.maxOrderQty != null ? { maxQty: product.maxOrderQty } : {}),
      },
    });
  }

  const cartValueCurrent = available.reduce((sum, row) => sum + row.currentPriceUah * row.qty, 0);
  return {
    sourcePublicOrderNumber,
    sourceOrderStatus: String(source.order.order_top_status ?? source.order.status ?? ""),
    sourcePaymentStatus: String(source.order.payment_status ?? ""),
    itemCountSource: source.items.length,
    available,
    unavailable,
    cartValueCurrent,
    deliveryCity: source.order.delivery_city ?? null,
    deliveryWarehouse: source.order.delivery_warehouse ?? null,
  };
}

/**
 * Merge current cart with reorder lines. Same catalog id increases qty.
 * NEVER copies payment_status / STG / bank match from the source order.
 */
export function mergeReorderIntoCart(existing: CartV2Item[], additions: ReorderAvailableItem[]): CartV2Item[] {
  const next: CartV2Item[] = existing.map((row) => ({ ...row }));
  for (const add of additions) {
    const found = next.find((row) => row.id === add.cartItem.id);
    if (found) {
      found.qty += add.qty;
      found.priceUah = add.cartItem.priceUah;
      found.title = add.cartItem.title;
      found.imageUrl = add.cartItem.imageUrl;
      if (add.cartItem.sku) found.sku = add.cartItem.sku;
      if (add.cartItem.maxQty != null) {
        found.maxQty = add.cartItem.maxQty;
        found.qty = Math.min(found.qty, add.cartItem.maxQty);
      }
    } else {
      next.push({ ...add.cartItem, qty: add.qty });
    }
  }
  return next.filter((row) => row.qty > 0 && row.priceUah > 0 && Boolean(row.id));
}

export function cartHasPaymentFields(items: CartV2Item[]): boolean {
  return items.some((row) =>
    Object.prototype.hasOwnProperty.call(row, "payment_status") ||
    Object.prototype.hasOwnProperty.call(row, "payment_confirmed_bank_iban") ||
    Object.prototype.hasOwnProperty.call(row, "bank_confirmed") ||
    Object.prototype.hasOwnProperty.call(row, "public_order_number"),
  );
}

function scopedSessionKey(): string {
  return `${SESSION_KEY}.scope.${currentIdentityScope()}`;
}

export function writeReorderSession(session: ReorderSessionV2): void {
  try {
    window.sessionStorage.setItem(scopedSessionKey(), JSON.stringify(session));
  } catch {
    /* ignore */
  }
}

export function peekReorderSession(): ReorderSessionV2 | null {
  try {
    const raw = window.sessionStorage.getItem(scopedSessionKey());
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ReorderSessionV2;
    if (!parsed || typeof parsed !== "object") return null;
    const stg = normalizePublicStg(parsed.sourcePublicOrderNumber);
    if (!stg) return null;
    return { ...parsed, sourcePublicOrderNumber: stg };
  } catch {
    return null;
  }
}

export function patchReorderSession(patch: Partial<ReorderSessionV2>): ReorderSessionV2 | null {
  const cur = peekReorderSession();
  if (!cur) return null;
  const next = { ...cur, ...patch };
  writeReorderSession(next);
  return next;
}

export function clearReorderSession(): void {
  try {
    window.sessionStorage.removeItem(scopedSessionKey());
  } catch {
    /* ignore */
  }
}

export function isActiveReorderSession(session: ReorderSessionV2 | null): boolean {
  return Boolean(session && !session.newPublicOrderNumber && !session.abandoned);
}

export function buildReorderSessionFromSnapshot(
  snapshot: ReorderSnapshot,
  cartHadItemsBefore: boolean,
): ReorderSessionV2 {
  return {
    sourcePublicOrderNumber: snapshot.sourcePublicOrderNumber,
    sourceOrderStatus: snapshot.sourceOrderStatus,
    sourcePaymentStatus: snapshot.sourcePaymentStatus,
    itemCountSource: snapshot.itemCountSource,
    itemCountAdded: snapshot.available.length,
    itemCountUnavailable: snapshot.unavailable.length,
    cartValueCurrent: snapshot.cartValueCurrent,
    unavailable: snapshot.unavailable,
    cartHadItemsBefore,
    deliveryCity: snapshot.deliveryCity,
    deliveryWarehouse: snapshot.deliveryWarehouse,
    startedAt: new Date().toISOString(),
    checkoutOpened: false,
    newPublicOrderNumber: null,
    abandoned: false,
  };
}

export function reorderCartBannerUk(session: ReorderSessionV2): string {
  if (session.itemCountAdded === 0) {
    return "Цього товару зараз немає в наявності";
  }
  if (session.cartHadItemsBefore) return "Кошик оновлено";
  return `Товари з замовлення ${session.sourcePublicOrderNumber} додано до кошика`;
}
